import { CommonModule, DatePipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { DropdownOption } from '../../../../../shared/components/ui/dropdown/dropdown.component';
import {
  IconComponent,
  IconName,
} from '../../../../../shared/icons/icon.component';
import { IconComponentV2 } from '../../../../../shared/components/icon-component/icon.component';
import { AiHeadlinesComponent } from '../../../../../shared/components/ai-headlines/ai-headlines.component';
import { MultiBarData } from '../../../../../shared/components/charts/multi-horizontal-bar-chart/multi-horizontal-bar-chart.component';
import { DonutChartComponent } from '../../../../../shared/components/charts/donut-chart/donut-chart.component';
import {
  PaginatedTableComponent,
  TableHeader,
} from '../../../../../shared/components/tables/paginated-table/paginated-table.component';
import { IndividualToasterComponent } from '../../../../../shared/components/loader/loader.component';
import {
  MobileSheetComponent,
  MobileSheetData,
} from '../../../../../shared/components/mobile-sheet/mobile-sheet.component';
import {
  NormalDatePickerComponent,
  DateRangePickerOutput,
  TimeUnit,
} from '../../../../../shared/components/simple-date-picker/normal-date-picker.component';
import { CommonService } from '../../../../../core/services/common.service';
import { MobileBackNavigationService } from '../../../../../core/services/mobile-back-navigation.service';

export interface MobilePlatformStat {
  key: string;
  label: string;
  formattedValue: string;
}

export interface MobilePlatformItem {
  platformName: string;
  accentColor: string;
  badgeBg: string;
  icon: string;
  primaryStat: MobilePlatformStat | null;
  secondaryStats: MobilePlatformStat[];
}

interface MobileOverallStatCard {
  icon: IconName;
  label: string;
  count: number | string;
  bg: string;
}

interface WatchTimeSummaryCard {
  rank: number;
  label: string;
  value: number;
  formattedValue: string;
  color: string;
  icon: string;
  background: string;
  borderColor: string;
}

@Component({
  selector: 'app-social-platform-mobile',
  standalone: true,
  imports: [
    CommonModule,
    IconComponent,
    IconComponentV2,
    AiHeadlinesComponent,
    DonutChartComponent,
    PaginatedTableComponent,
    IndividualToasterComponent,
  ],
  templateUrl: './social-platform-mobile.component.html',
  providers: [DatePipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    .tabs-container {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      /* No left padding — the first platform tab should be flush with the
         horizontal start of the card. Right padding kept so the last tab
         doesn't bump into the edge fade mask. */
      padding: 0.375rem 0;
      overflow-x: auto;
      white-space: nowrap;
      scroll-behavior: smooth;
      -webkit-overflow-scrolling: touch;
      scroll-snap-type: x mandatory;
      mask-image: none;
      -webkit-mask-image: none;
      transition: all 0.3s ease;
    }

    .tabs-container::-webkit-scrollbar {
      display: none;
    }

    .tabs-container {
      -ms-overflow-style: none;
      scrollbar-width: none;
    }

    .tabs-container.mask-left.mask-right {
      -webkit-mask-image: linear-gradient(to right, transparent, black 40px, black calc(100% - 40px), transparent);
      mask-image: linear-gradient(to right, transparent, black 40px, black calc(100% - 40px), transparent);
    }

    .tabs-container.mask-left:not(.mask-right) {
      -webkit-mask-image: linear-gradient(to right, transparent, black 40px, black);
      mask-image: linear-gradient(to right, transparent, black 40px, black);
    }

    .tabs-container.mask-right:not(.mask-left) {
      -webkit-mask-image: linear-gradient(to right, black, black calc(100% - 40px), transparent);
      mask-image: linear-gradient(to right, black, black calc(100% - 40px), transparent);
    }

    .tabs-container button {
      scroll-snap-align: start;
      flex-shrink: 0;
    }
  `],
})
export class SocialPlatformMobileComponent implements AfterViewInit, OnDestroy {
  @ViewChild('platformTabsContainer')
  platformTabsContainer?: ElementRef<HTMLDivElement>;
  @ViewChildren('platformTabButton')
  platformTabButtons?: QueryList<ElementRef<HTMLButtonElement>>;
  // inputs
  startDate = input<string | null>(null);
  endDate = input<string | null>(null);
  view = input<'analyticsView' | 'tableView'>('analyticsView');
  tabOptions = input<any[]>([]);
  cardStatsConfig = input<any[]>([]);
  isOverallLoading = input(false);
  isPlatformWiseLoading = input(false);

  isDonutChartLoading = input(false);
  isLoading = input(false);
  platformItemsData = input<MobilePlatformItem[]>([]);
  filteredPlatforms = input<DropdownOption[]>([]);
  selectedPlatformTypeNames = input<string[]>([]);
  selectedPlatforms = input<any[]>([]);
  hasAnyChartData = input(false);
  platformList = input<any[]>([]);
  multiHorizontalBarChartData = input<MultiBarData[]>([]);
  topThreeWatchTimeData = input<any[]>([]);
  donutData = input<any[]>([]);
  platformOptions = input<DropdownOption[]>([]);
  processedStatsData = input<any[]>([]);
  
  tableHeaders = input<TableHeader[]>([]);
  tableActionOptions = input<any[]>([]);
  totalItems = input(0);
  predefinedRanges = input<any[]>([]);
  selectedStartRange = input<string | null>(null);
  selectedEndRange = input<string | null>(null);

// filters & outputs
  openPanelRequested = output<void>();
  viewChanged = output<'analyticsView' | 'tableView'>();
  dateRangeChanged = output<any>();
  platformTypeSelected = output<DropdownOption>();
  platformFilterChanged = output<string | string[] | null>();
  dateFilterChanged = output<any>();
  clearFiltersRequested = output<void>();
  tableActionSelected = output<any>();

  private _bottomSheet = inject(MatBottomSheet);
  private _commonService = inject(CommonService);
  private _mobileBackNav = inject(MobileBackNavigationService);
  private datePipe = inject(DatePipe);

  private readonly FOLLOWERS_COMPARISON_BACK_ID = 'followersComparison';

  // signals & scroll
  readonly canScrollLeft = signal(false);
  readonly canScrollRight = signal(false);
  readonly selectedPlatformName = signal<string | null>(null);
  readonly showFollowersComparison = signal(false);

  //  filter state 
  private readonly DEFAULT_PRESET_LABEL = 'Last 3 Months';
  private readonly DEFAULT_UNIT: TimeUnit = 'month';
  private readonly lastUnit = signal<TimeUnit>(this.DEFAULT_UNIT);
  private readonly lastPresetLabel = signal<string>(this.DEFAULT_PRESET_LABEL);
  private readonly lastStartDate = signal<string | null>(null);
  private readonly lastEndDate = signal<string | null>(null);
  @ViewChild('followersComparisonSection') followersComparisonSection?: ElementRef<HTMLDivElement>;
  readonly displayStartDate = computed<string | null>(
    () => this.lastStartDate() ?? this.startDate()
  );
  readonly displayEndDate = computed<string | null>(
    () => this.lastEndDate() ?? this.endDate()
  );

  toggleFollowersComparison() {
    if (this.showFollowersComparison()) { 
      if (this._mobileBackNav.isActive(this.FOLLOWERS_COMPARISON_BACK_ID)) {
        this._mobileBackNav.pop(this.FOLLOWERS_COMPARISON_BACK_ID);
      } else {
        this.closeFollowersComparison();
      }
    } else {
      this.openFollowersComparison();
    }
  }

  private openFollowersComparison(): void {
    this.showFollowersComparison.set(true);
    this.applyFollowersComparisonChrome(true);

    // Guard against accidental double-push (e.g. rapid double taps on iOS),
    // which would otherwise require multiple back presses to dismiss.
    if (!this._mobileBackNav.isActive(this.FOLLOWERS_COMPARISON_BACK_ID)) {
      this._mobileBackNav.push(this.FOLLOWERS_COMPARISON_BACK_ID, () => {
        this.closeFollowersComparison();
      });
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.followersComparisonSection?.nativeElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    });
  }

  private closeFollowersComparison(): void {
    this.showFollowersComparison.set(false);
    this.applyFollowersComparisonChrome(false);
  }
  isDarkMode = document.documentElement.classList.contains('dark');
  private applyFollowersComparisonChrome(active: boolean): void {
    if (active) {
      // Hide the mobile header (product/Denefits dropdown) and the bottom nav 
      this._commonService.activeDetailedView.set('followersByPlatform');
      this._commonService.forceHideMobileNav.set(true);
      this._commonService.showMobileNav.set(false);
      this._commonService.forceHideMobileFloatingBar.set(true);
      this._commonService.showMobileFloatingBar.set(false);
    } else {
      // Restore default chrome when leaving the detailed view.
      if (this._commonService.activeDetailedView() === 'followersByPlatform') {
        this._commonService.activeDetailedView.set(null);
      }
      this._commonService.forceHideMobileNav.set(false);
      this._commonService.showMobileNav.set(true);
      this._commonService.forceHideMobileFloatingBar.set(false);
      this._commonService.showMobileFloatingBar.set(true);
    }
  }

  readonly platformItems = computed(() => this.platformItemsData());

  readonly selectedPlatform = computed<MobilePlatformItem | null>(() => {
    const items = this.platformItems();
    const selected = this.selectedPlatformName();

    if (!items.length) return null;
    return items.find((item) => item.platformName === selected) ?? items[0];
  });

  readonly primaryPlatformStat = computed(() => this.selectedPlatform()?.primaryStat ?? null);
  readonly secondaryPlatformStats = computed(() => this.selectedPlatform()?.secondaryStats ?? []);
  // mobile Overall Performance stats section start
  readonly mobileOverallStats = computed<MobileOverallStatCard[]>(() =>
    this.cardStatsConfig()
  );
  readonly totalWatchTimeDisplay = computed(() => {
    const watchTimeStat = this.mobileOverallStats().find((stat) =>
      stat.label.includes('Watch Time')
    );

    return watchTimeStat ? this.formatWatchTimeHours(watchTimeStat.count) : '0';
  }); 
  private readonly watchTimePinnedOrder: ReadonlyArray<{
    label: string;
    icon: string;
    background?: string;
  }> = [
    { label: 'Facebook', icon: 'img/social-icon/facebook-icon.svg' },
    {
      label: 'YouTube',
      icon: 'img/social-icon/youtube-rectangle-icon.svg',
      background: '#F0505033',
    },
  ];

  readonly watchTimeSummaryCards = computed<WatchTimeSummaryCard[]>(() => {
    const platforms = this.platformItems();
    const source = this.topThreeWatchTimeData();

    // Facebook (rank #1) keeps  
    const facebookMatch = platforms.find(
      (platform) => platform.platformName === 'Facebook'
    );
    const facebookBackground = facebookMatch?.badgeBg || '#EEF5FE';

    const buildCard = (
      item: any,
      rank: number,
      label: string,
      iconOverride: string,
      backgroundOverride?: string
    ): WatchTimeSummaryCard => {
      const platformMatch = platforms.find(
        (platform) => platform.platformName === label
      );
      const color = platformMatch?.accentColor || item?.color || '#AD46FF';
      const background =
        backgroundOverride ||
        (label === 'Facebook' ? facebookBackground : platformMatch?.badgeBg) ||
        '#F8FAFC';

      return {
        rank,
        label,
        value: Number(item?.value ?? 0),
        formattedValue: this.formatWatchTimeHours(item?.value ?? 0),
        color,
        icon: iconOverride || platformMatch?.icon || '',
        background,
        borderColor: this.withOpacity(color, 0.18),
      };
    };
 
    return this.watchTimePinnedOrder.map((pinned, idx) => {
      const match = source.find(
        (item: any) =>
          String(item?.label).toLowerCase() === pinned.label.toLowerCase()
      );
      return buildCard(
        match ?? null,
        idx + 1,
        pinned.label,
        pinned.icon,
        pinned.background
      );
    });
  });

  readonly totalFollowers = computed(() => {
    const total = this.donutData().reduce(
      (sum: number, item: any) => sum + Number(item?.value ?? 0),
      0
    );
    return total.toLocaleString('en-US');
  });

  private readonly platformIconMap: Record<string, string> = {
    facebook: 'img/social-icon/facebook-square.svg',
    instagram: 'img/social-icon/instagram-icon.svg',
    linkedin: 'img/social-icon/linkedin-icon.svg',
    pinterest: 'img/social-icon/pintrest-square-icon.svg',
    snapchat: 'img/social-icon/snapchat-icon.svg',
    tiktok: 'img/social-icon/tiktok-square-icon.svg',
    youtube: 'img/social-icon/youtube-icon.svg',
  };
 
  private readonly brandTilePlatforms = new Set<string>([
    'facebook',
    'tiktok',
    'pinterest',
  ]);

  isBrandTileIcon(label: string | null | undefined): boolean {
    return this.brandTilePlatforms.has(String(label ?? '').toLowerCase());
  }

  // ------------------------------------------------------------------
  // Donut chart label layout (Followers By Platform)
  // ------------------------------------------------------------------
  // The shared <app-donut-chart> places its percentage labels on a
  // circle just outside the donut and only relaxes them vertically when
  // they collide. With real DB data we frequently get a cluster of
  // tiny slices near the top (e.g. 0% / 1% / 3% for Pinterest /
  // LinkedIn / YouTube) whose Y values get pushed apart but whose X
  // values stay tiny — so the percentage text ends up sitting on top
  // of the donut ring. We disable the chart's built-in labels
  // (`[showValue]="false"`) and render our own here, mirroring the
  // chart's geometry and re-projecting each label back onto the
  // label-arc circle after relaxation. Scoped to this component only.
  private readonly DONUT_CHART_WIDTH = 320;
  private readonly DONUT_CHART_HEIGHT = 280;
  private readonly DONUT_RADIUS_SCALE = 0.85;
  private readonly DONUT_OUTER_RADIUS_RATIO = 0.75;
  private readonly DONUT_LABEL_OFFSET = 32;
  private readonly DONUT_LABEL_NUDGE = 12;
  private readonly DONUT_LABEL_SPACING = 26;

  readonly donutChartViewBox = `0 0 ${this.DONUT_CHART_WIDTH} ${this.DONUT_CHART_HEIGHT}`;
  readonly donutChartCenterTransform = `translate(${this.DONUT_CHART_WIDTH / 2}, ${this.DONUT_CHART_HEIGHT / 2})`;

  readonly donutLabels = computed(() => {
    const data = this.donutData().filter(
      (d: any) => Number(d?.value ?? 0) > 0
    );
    const total = data.reduce(
      (sum: number, d: any) => sum + Number(d?.value ?? 0),
      0
    );
    if (total <= 0 || data.length === 0) return [];

    const radius =
      (Math.min(this.DONUT_CHART_WIDTH, this.DONUT_CHART_HEIGHT) / 2) *
      this.DONUT_RADIUS_SCALE;
    const outerRadius = radius * this.DONUT_OUTER_RADIUS_RATIO;
    const labelArcRadius = outerRadius + this.DONUT_LABEL_OFFSET;

    type DonutLabel = {
      isRight: boolean;
      initialY: number;
      x: number;
      y: number;
      color: string;
      text: string;
    };

    let cumulative = 0;
    const labels: DonutLabel[] = data.map((d: any) => {
      const value = Number(d?.value ?? 0);
      const startAngle = (cumulative / total) * 2 * Math.PI;
      cumulative += value;
      const endAngle = (cumulative / total) * 2 * Math.PI;
      const midAngle = startAngle + (endAngle - startAngle) / 2;
      const isRight = midAngle < Math.PI;
      const x = Math.sin(midAngle) * labelArcRadius;
      const y = -Math.cos(midAngle) * labelArcRadius;
      const percent = (value / total) * 100;
      return {
        isRight,
        initialY: y,
        x,
        y,
        color: d?.color || '#7D7D93',
        text: `${percent.toFixed(0)}%`,
      };
    });

    const rightLabels = labels
      .filter((l) => l.isRight)
      .sort((a, b) => a.initialY - b.initialY);
    const leftLabels = labels
      .filter((l) => !l.isRight)
      .sort((a, b) => a.initialY - b.initialY);

    const spacing = this.DONUT_LABEL_SPACING;
    const relax = (group: DonutLabel[]) => {
      for (let i = 1; i < group.length; i++) {
        if (group[i].y - group[i - 1].y < spacing) {
          group[i].y = group[i - 1].y + spacing;
        }
      }
    };
    relax(rightLabels);
    relax(leftLabels);

    // Re-project each label radially back onto the label-arc circle so
    // its anchor stays at distance `labelArcRadius` from the centre.
    // Without this, narrow-slice labels whose Y is shifted by the
    // relax pass keep their original (small) X and end up overlapping
    // the donut ring — exactly the bug we are fixing.
    const rSq = labelArcRadius * labelArcRadius;
    const reproject = (group: DonutLabel[]) => {
      for (const l of group) {
        const remaining = rSq - l.y * l.y;
        const baseX = remaining > 0 ? Math.sqrt(remaining) : 0;
        l.x = l.isRight
          ? baseX + this.DONUT_LABEL_NUDGE
          : -baseX - this.DONUT_LABEL_NUDGE;
      }
    };
    reproject(rightLabels);
    reproject(leftLabels);

    return [...rightLabels, ...leftLabels];
  });

  readonly followersByPlatformList = computed(() => {
    const platforms = this.platformItems();

    return this.donutData()
      .map((item: any) => {
        const labelKey = String(item?.label ?? '').toLowerCase();
        const platformMatch = platforms.find(
          (p) => p.platformName?.toLowerCase() === labelKey
        );
        return {
          label: item?.label ?? '',
          value: Number(item?.value ?? 0),
          formattedValue: Number(item?.value ?? 0).toLocaleString('en-US'),
          color: item?.color || platformMatch?.accentColor || '#AD46FF',
          icon:
            platformMatch?.icon || this.platformIconMap[labelKey] || '',
          background: platformMatch?.badgeBg || '#F5F5F5',
        };
      })
      .sort((a, b) => b.value - a.value);
  });

  private resizeObserver?: ResizeObserver;

  constructor() {
    effect(() => {
      const items = this.platformItems();
      const selected = this.selectedPlatformName();

      if (!items.length) {
        if (selected !== null) {
          this.selectedPlatformName.set(null);
        }
        return;
      }

      if (!selected || !items.some((item) => item.platformName === selected)) {
        this.selectedPlatformName.set(items[0].platformName ?? null);
      }

      queueMicrotask(() => this.updateScrollState());
    });
  }

  requestOpenPanel() {
    this.openPanelRequested.emit();
  }

  ngAfterViewInit(): void {
    this.syncSelectedPlatform();

    setTimeout(() => {
      this.scrollSelectedPlatformIntoView();
      this.updateScrollState();
    }, 0);

    const tabsElement = this.platformTabsContainer?.nativeElement;
    if (tabsElement) {
      this.resizeObserver = new ResizeObserver(() => this.updateScrollState());
      this.resizeObserver.observe(tabsElement);
    }

    this.platformTabButtons?.changes.subscribe(() => {
      this.syncSelectedPlatform();
      setTimeout(() => {
        this.scrollSelectedPlatformIntoView();
        this.updateScrollState();
      }, 0);
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    if (this.showFollowersComparison()) {
      this.applyFollowersComparisonChrome(false);
    }
    this._mobileBackNav.cleanup(this.FOLLOWERS_COMPARISON_BACK_ID);
  }

  openFilterSheet() {
    this._commonService.forceHideMobileNav.set(true);
    this._commonService.showMobileNav.set(false);
    this._commonService.forceHideMobileFloatingBar.set(true);
    this._commonService.showMobileFloatingBar.set(false);

    // Prefer the user's last selection; fall back to the parent-provided range.
    const effectiveStart = this.lastStartDate() ?? this.selectedStartRange();
    const effectiveEnd = this.lastEndDate() ?? this.selectedEndRange();

    const startStr = this.datePipe.transform(effectiveStart, 'MMM dd, yyyy');
    const endStr = this.datePipe.transform(effectiveEnd, 'MMM dd, yyyy');
    const dateSubtitle = startStr && endStr ? `${startStr} - ${endStr}` : '';

    const sheetRef = this._bottomSheet.open(MobileSheetComponent, {
      panelClass: 'mobile-sheet-wrapper-container',
      data: {
        title: 'Filter',
        subtitle: dateSubtitle,
        icon: 'calendar',
        component: NormalDatePickerComponent,
        data: {
          isMobileMode: true,
          predefinedRangesDataSets: this.predefinedRanges(),
          showComparison: false,
          allowComparison: false,
          showGroupBy: true,
          hideWeekDayUnit: true,
          hideRightSection: true,
          defaultTimeUnit: this.DEFAULT_UNIT,
          allowedGroupByUnits: [this.DEFAULT_UNIT],
          alwaysAvailableUnit: this.DEFAULT_UNIT,
          selectedStartRange: effectiveStart,
          selectedEndRange: effectiveEnd,
          // Preserve the previously chosen preset label so the "Choose Period"
          // trigger doesn't fall back to "Tap to Select" when the saved
          // start/end snapshot no longer matches any preset's freshly-computed
          // range (e.g. relative ranges shifting after midnight on iOS).
          selectedPresetLabel: this.lastPresetLabel(),
        },
      } as MobileSheetData,
    });

    sheetRef.afterDismissed().subscribe((result: DateRangePickerOutput) => {
      this._commonService.forceHideMobileNav.set(false);
      this._commonService.showMobileNav.set(true);
      this._commonService.forceHideMobileFloatingBar.set(false);
      this._commonService.showMobileFloatingBar.set(true);

      if (!result) return;
 
      const isReset = result.preset_label === this.DEFAULT_PRESET_LABEL;

      this.lastUnit.set(result.unit || this.DEFAULT_UNIT);
      this.lastPresetLabel.set(result.preset_label || 'Custom');
      this.lastStartDate.set(isReset ? null : result.start_date ?? null);
      this.lastEndDate.set(isReset ? null : result.end_date ?? null);

      this.dateRangeChanged.emit({ ...result });
    });
  }

  formatOverallStatCount(count: number | string): string {
    const numericValue =
      typeof count === 'number'
        ? count
        : Number(String(count).replace(/,/g, '').trim());

    if (!Number.isFinite(numericValue)) {
      return String(count ?? '');
    }

    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: numericValue >= 1000000 ? 1 : 0,
    }).format(numericValue);
  }

  formatWatchTimeHours(value: number | string): string {
    const numericValue =
      typeof value === 'number'
        ? value
        : Number(String(value).replace(/,/g, '').trim());

    if (!Number.isFinite(numericValue)) {
      return String(value ?? '');
    }

    return numericValue.toLocaleString('en-US', {
      maximumFractionDigits: 0,
    });
  }

  private withOpacity(color: string, alpha: number): string {
    const normalized = color?.replace('#', '') ?? '';

    if (normalized.length !== 6) {
      return color;
    }

    const r = Number.parseInt(normalized.slice(0, 2), 16);
    const g = Number.parseInt(normalized.slice(2, 4), 16);
    const b = Number.parseInt(normalized.slice(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  selectPlatform(platformName: string | null | undefined) {
    if (!platformName) return;

    this.selectedPlatformName.set(platformName);
    setTimeout(() => {
      this.scrollSelectedPlatformIntoView();
      this.updateScrollState();
    }, 0);
  }

  updateScrollState() {
    const el = this.platformTabsContainer?.nativeElement;
    if (!el) {
      this.canScrollLeft.set(false);
      this.canScrollRight.set(false);
      return;
    }

    const currentScroll = el.scrollLeft;
    this.canScrollLeft.set(currentScroll > 10);
    this.canScrollRight.set(
      currentScroll + el.clientWidth < el.scrollWidth - 10
    );
  }

  requestViewChange(view: string) {
    if (view === 'analyticsView' || view === 'tableView') {
      this.viewChanged.emit(view);
    }
  }

  requestDateRangeChange(value: any) {
    this.dateRangeChanged.emit(value);
  }

  requestTableAction(action: any) {
    this.tableActionSelected.emit(action);
  }

  formatCardCount(stat: any): string {
    if (stat?.label?.includes('Watch Time')) {
      return String(stat?.count ?? '0');
    }

    const numeric = Number(stat?.count ?? 0);
    return Number.isFinite(numeric) ? numeric.toLocaleString() : String(stat?.count ?? '0');
  }

  private syncSelectedPlatform() {
    const items = this.platformItems();
    const selected = this.selectedPlatformName();

    if (!items.length) {
      this.selectedPlatformName.set(null);
      return;
    }

    if (!selected || !items.some((item) => item.platformName === selected)) {
      this.selectedPlatformName.set(items[0].platformName ?? null);
    }
  }

  private scrollSelectedPlatformIntoView() {
    const selected = this.selectedPlatformName();
    if (!selected) return;

    const button = this.platformTabButtons
      ?.toArray()
      .find((ref) => ref.nativeElement.dataset['platform'] === selected);

    button?.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
  }
}
